import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Generate unique session ID
const generateSessionId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Get device info
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  
  let device_type = 'desktop';
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    device_type = 'mobile';
  } else if (/iPad|Android(?=.*\bMobile\b)/i.test(userAgent)) {
    device_type = 'tablet';
  }

  let browser = 'unknown';
  if (userAgent.includes('Chrome')) browser = 'Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Firefox';
  else if (userAgent.includes('Safari')) browser = 'Safari';
  else if (userAgent.includes('Edge')) browser = 'Edge';

  let os = 'unknown';
  if (userAgent.includes('Windows')) os = 'Windows';
  else if (userAgent.includes('Mac')) os = 'macOS';
  else if (userAgent.includes('Linux')) os = 'Linux';
  else if (userAgent.includes('Android')) os = 'Android';
  else if (userAgent.includes('iOS')) os = 'iOS';

  return { device_type, browser, os };
};

// Get UTM parameters
const getUtmParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
  };
};

export const useAnalytics = () => {
  const sessionId = useRef<string>(generateSessionId());
  const sessionStarted = useRef<boolean>(false);
  const pageStartTime = useRef<number>(Date.now());
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize session
  const initializeSession = async () => {
    if (sessionStarted.current) return;
    
    const deviceInfo = getDeviceInfo();
    const utmParams = getUtmParams();
    
    try {
      await supabase.from('analytics_sessions').insert({
        session_id: sessionId.current,
        referrer: document.referrer,
        ...utmParams,
        ...deviceInfo,
      });
      
      sessionStarted.current = true;
      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to initialize analytics session:', error);
    }
  };

  // Track event
  const trackEvent = async (eventType: string, eventName: string, properties?: Record<string, any>) => {
    if (!sessionStarted.current) {
      await initializeSession();
    }

    const deviceInfo = getDeviceInfo();
    const utmParams = getUtmParams();

    try {
      await supabase.from('analytics_events').insert({
        session_id: sessionId.current,
        event_type: eventType,
        event_name: eventName,
        page_url: window.location.href,
        page_title: document.title,
        properties: properties || {},
        user_agent: navigator.userAgent,
        referrer: document.referrer,
        ...utmParams,
        ...deviceInfo,
      });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  };

  // Track page view
  const trackPageView = async (pagePath?: string) => {
    const path = pagePath || window.location.pathname;
    await trackEvent('page_view', 'Page View', {
      path,
      title: document.title,
      time_on_previous_page: Date.now() - pageStartTime.current,
    });
    pageStartTime.current = Date.now();
  };

  // Track click
  const trackClick = async (element: string, text?: string, href?: string) => {
    await trackEvent('click', 'Click', {
      element,
      text,
      href,
      timestamp: Date.now(),
    });
  };

  // Track form interaction
  const trackFormInteraction = async (formType: string, action: string, field?: string) => {
    await trackEvent('form', 'Form Interaction', {
      form_type: formType,
      action,
      field,
      timestamp: Date.now(),
    });
  };

  // Track form submission
  const trackFormSubmission = async (formType: string, formData: Record<string, any>) => {
    try {
      // Track the event
      await trackEvent('form', 'Form Submission', {
        form_type: formType,
        timestamp: Date.now(),
      });

      // Store form submission data
      const utmParams = getUtmParams();
      await supabase.from('form_submissions').insert({
        session_id: sessionId.current,
        form_type: formType,
        form_data: formData,
        page_url: window.location.href,
        referrer: document.referrer,
        ...utmParams,
        status: 'submitted',
      });
    } catch (error) {
      console.error('Failed to track form submission:', error);
    }
  };

  // Track scroll depth
  const trackScrollDepth = async (depth: number) => {
    await trackEvent('engagement', 'Scroll Depth', {
      depth_percentage: depth,
      timestamp: Date.now(),
    });
  };

  // Track time on page
  const trackTimeOnPage = async () => {
    const timeSpent = Date.now() - pageStartTime.current;
    await trackEvent('engagement', 'Time on Page', {
      time_spent_ms: timeSpent,
      time_spent_seconds: Math.round(timeSpent / 1000),
    });
  };

  // Initialize on mount
  useEffect(() => {
    initializeSession();
    trackPageView();

    // Track time on page when leaving
    const handleBeforeUnload = () => {
      trackTimeOnPage();
    };

    // Track scroll depth
    let maxScrollDepth = 0;
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollDepth = Math.round((scrollTop / documentHeight) * 100);
      
      if (scrollDepth > maxScrollDepth && scrollDepth % 25 === 0) {
        maxScrollDepth = scrollDepth;
        trackScrollDepth(scrollDepth);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Track page changes for SPAs
  useEffect(() => {
    trackPageView();
  }, [window.location.pathname]);

  return {
    trackEvent,
    trackPageView,
    trackClick,
    trackFormInteraction,
    trackFormSubmission,
    trackScrollDepth,
    trackTimeOnPage,
    sessionId: sessionId.current,
    isInitialized,
  };
};